import {
  Chip,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
} from '@mui/material';
import { InlineStatus } from 'components/InlineStatus';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { useSelectedLocationGeoEventId } from 'features/locations/hooks/useSelectedLocationGeoEventId';
import { WEEK_HOURS_DAYS } from 'features/locations/location.constants';
import { HourStatus, Location } from 'features/locations/location.types';
import { getOperatingHoursForViewer } from 'features/locations/utils/operatingHours';
import { useTranslation } from 'react-i18next';
import {
  formatSecondsSinceMidnight,
  getDayOfWeek,
  getTimeAgo,
} from 'shared/dates';

const statusToLabelTransKey: Record<HourStatus, string> = {
  open: 'hoursList.status.open',
  closed: 'hoursList.status.closed',
  closingSoon: 'hoursList.status.closingSoon',
};

export const HoursList = (): JSX.Element => {
  const { t } = useTranslation();
  const geoEventId = useSelectedLocationGeoEventId();
  const {
    geoEvent: location,
    isGeoEventLoading,
    isGeoEventFetchError,
  } = useGeoEvent<Location>({
    id: geoEventId,
    useCacheBuster: true,
  });

  if (isGeoEventLoading || isGeoEventFetchError || !location) {
    return <InlineStatus status={isGeoEventLoading ? 'loading' : 'error'} />;
  }

  const {
    hoursInUserTimeZone,
    todayStatus: currentHourStatus,
    viewerDayOfWeek: currentUserDay,
  } = getOperatingHoursForViewer(location.data.hours, location.data.timezone);

  return (
    <List
      subheader={
        <ListSubheader disableGutters sx={{ lineHeight: 1.2, px: 2, pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="h3"
              color="textPrimary"
              sx={{ fontWeight: 'bold' }}
            >
              {t('hoursList.title')}
            </Typography>
            {Boolean(location.dateModified) && (
              <Typography variant="body2">
                {t('hoursList.updated', {
                  timeAgo: getTimeAgo(location.dateModified),
                })}
              </Typography>
            )}
          </Stack>
        </ListSubheader>
      }
    >
      {WEEK_HOURS_DAYS.map((dayOfWeek) => {
        const dayHour = hoursInUserTimeZone.find(
          (h) => h.dayOfWeek === dayOfWeek,
        );
        const isCurrentDay = dayOfWeek === currentUserDay;
        const hasTimes = dayHour?.times && dayHour.times.length > 0;
        return (
          <ListItem key={`day-${dayOfWeek}`} divider>
            <ListItemText
              primary={
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  component="span"
                >
                  <Typography component="span">
                    {getDayOfWeek(dayOfWeek, 'eee')}
                  </Typography>

                  {isCurrentDay && hasTimes && (
                    <Chip
                      label={t(statusToLabelTransKey[currentHourStatus])}
                      variant="watchduty"
                      color={currentHourStatus === 'open' ? 'success' : 'error'}
                    />
                  )}
                </Stack>
              }
              secondary={
                hasTimes ? (
                  <Stack spacing={1} alignItems="flex-end" component="span">
                    {dayHour.times.map((time) => {
                      return (
                        <Typography
                          key={time.openTime + time.closeTime}
                          variant="body1"
                          fontWeight="fontWeightMedium"
                          color="textPrimary"
                          sx={{ textTransform: 'uppercase' }}
                          component="span"
                        >
                          {`${formatSecondsSinceMidnight(time.openTime)} - ${formatSecondsSinceMidnight(time.closeTime)}`}
                        </Typography>
                      );
                    })}
                  </Stack>
                ) : (
                  <Chip
                    label={t('hoursList.status.closed')}
                    variant="watchduty"
                    color="error"
                    component="span"
                  />
                )
              }
              sx={{ display: 'flex', flexDirection: 'row' }}
              slotProps={{
                primary: { sx: { flexGrow: 1 } },
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );
};
